import { useCallback } from 'react';
import isNil from 'lodash/isNil';
import { useAppDispatch } from '../../../../../redux/hooks';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { IAnalysisQueryResponse } from '../../../../../utils/apollo/responseTypes';
import { assertCondition, assertNotNil } from '../../../../../utils/types';
import { IDisplayedMeasure } from '../../../interfaces';
import { ISaveCompoundMeasureInput, ISaveSimpleMeasureInput } from './types';
import {
  ExpressionModeKey,
  IMeasureFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';

/**
 * User should be able to save a measure from measure controls options.
 *
 * There are 2 different options:
 * 1). saving Result measure (compound);
 * 2). saving Simple measure (basic or named).
 *
 * For Result measure we use measures array from Compound measure and a formula
 * for Save Measure modal. Without formula Compound measure can't be saved!
 *
 * For Simple measure case we have to find a related measure from analysis query
 * and then use it for Save Measure modal. Measure can be saved with a formula
 */
interface ISaveMeasureArgs {
  displayedMeasure: IDisplayedMeasure;
  onSubmit: (newMeasure: IMeasureFieldsFragment) => void;
  hideCalculation: boolean;
}

export const useSaveMeasure = (builderInfo: IAnalysisQueryResponse) => {
  const dispatch = useAppDispatch();

  return useCallback(
    (args: ISaveMeasureArgs) => {
      const { displayedMeasure, onSubmit, hideCalculation } = args;

      assertCondition(builderInfo.__typename === 'ExpressionQuery');

      const { measure, expressionMode } = builderInfo;
      if (measure.__typename === 'SimpleMeasure') {
        const { formula, version } = measure;

        dispatch(
          openModal({
            type: ModalSliceType.SaveMeasure,
            data: {
              type: 'simple',
              simpleMeasure: measure,
              formulaExpression: formula,
              isNamed: !isNil(version?.versionId),
              format: displayedMeasure.format,
              onSubmit,
              hideCalculation,
            } satisfies ISaveSimpleMeasureInput,
          }),
        );
      } else if (measure.__typename === 'CompoundMeasure') {
        const { measures } = measure;

        if (
          measure?.formula &&
          (displayedMeasure.isResultKey ||
            // special case: histogram occurrence is always compound measure
            // when measure.formula is not null
            expressionMode.key === ExpressionModeKey.Histogram)
        ) {
          dispatch(
            openModal({
              type: ModalSliceType.SaveMeasure,
              data: {
                type: 'compound',
                measures,
                formulaExpression: measure.formula,
                format: displayedMeasure.format,
                onSubmit,
                hideCalculation,
              } satisfies ISaveCompoundMeasureInput,
            }),
          );
        } else {
          const relatedMeasure = measures.find(
            (simpleMeasure) => simpleMeasure.key === displayedMeasure.key,
          );

          assertNotNil(relatedMeasure);

          const { version } = relatedMeasure;

          dispatch(
            openModal({
              type: ModalSliceType.SaveMeasure,
              data: {
                type: 'simple',
                simpleMeasure: relatedMeasure,
                formulaExpression: null,
                isNamed: !isNil(version?.versionId),
                format: displayedMeasure.format,
                onSubmit,
                hideCalculation,
              } satisfies ISaveSimpleMeasureInput,
            }),
          );
        }
      }
    },
    [builderInfo, dispatch],
  );
};
